import { FC, useContext } from 'react'
import { MdArrowDropDown } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { convertAmountToPeriod, getItemAmountForPeriod } from '../../../../lib/Calculator'
import { Spend } from '../../../../lib/Types'
import { SpendFrequency } from '../../lib/Enums/SpendFrequency'
import { useFormatCurrency } from '../../../../lib/CurrencyFormatter'
import { formatDate } from '../../../../lib/Dates'
import ReconciliationColumn from './ReconciliationColumn'
import { BudgetContext } from '../../../../lib/Context'

interface Props {
  spend: Spend
  isChild?: boolean
  showChildren?: boolean
  toggleChildren?: (state?: boolean) => void
}

const calculateAmountForReconciliationYear = (spend: Spend, year: number) => {
  // If spend was deleted within the year
  if (spend.deleted_date) {
    const deletedDate = new Date(spend.deleted_date)

    // First convert the latest spend amount to a monthly period
    const amountPerMonth = convertAmountToPeriod(spend.amounts.at(-2)!, SpendFrequency.MONTHLY)

    // Then calculate the amount for the year
    return amountPerMonth * (deletedDate.getMonth() + 1)
  }

  return getItemAmountForPeriod(spend, SpendFrequency.YEARLY)
}

const ReconciliationSpendRow: FC<Props> = ({
  spend,
  isChild = false,
}) => {
  const { t } = useTranslation(['budget'])
  const formatCurrency = useFormatCurrency()
  const { reconciliationYear } = useContext(BudgetContext)

  const hasChildren = () => {
    return spend.children && spend.children.length > 0
  }

  return (
    <>
      <table
        className={'min-w-full table-budget col-3'}
      >
        <tbody>
          <tr
            className={`bg-white ${isChild ? '' : 'border-b'} border-gray-300`}
          >
            <td className='text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap'>
              {hasChildren() && (
                <MdArrowDropDown
                  size='1.25rem'
                  className='float-left'
                />
              )}
              <div className='flex items-center'>
                <div
                  className={`flex flex-col grow ${hasChildren() ? 'cursor-pointer' : ''}`}
                >
                  <p className={`${isChild ? 'ml-5' : ''}`}>{spend.title}</p>
                  {spend.deleted_date && (
                    <p className={`italic ${isChild ? 'ml-5' : ''}`}>
                      {t('budget:reconciliationSpendRow.deletedOn', {
                        date: formatDate(new Date(spend.deleted_date), 'mm/dd yyyy'),
                      })}
                    </p>
                  )}
                </div>
              </div>
            </td>
            <td className='text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap'>
              {(!hasChildren()) && (formatCurrency(calculateAmountForReconciliationYear(spend, reconciliationYear)))}
            </td>
            <td className='text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap'>
              {!hasChildren() && <ReconciliationColumn spend={spend} />}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  )
}

export default ReconciliationSpendRow
